/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react'
import { Fragment, MouseEvent, ReactElement, useContext, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer'
import ProductComponent from './ProductComponent'
import { FeaturedArticleShowContext } from '../../FeaturedArticles/Show'
import { genJyuuGothicBold } from '../../fonts/FontFace'
import { ProductsComponentProp } from '../../types'

type Props = {
  component: ProductsComponentProp
  key: number
}

const ProductsComponentDivider = (): ReactElement => {
  return (
    <path
      d="M-3.04058e-08 17.6522C13.5 18.2758 26.95 17.8441 40.35 15.8774C56.15 13.575 72.1 13.3831 88 14.8701C98.35 15.8294 108.7 17.1725 119.1 17.7482C131.7 18.4677 144.3 17.6043 156.8 15.6855C169 13.8148 181.3 13.8627 193.65 13.9587C203.85 14.0546 213.95 15.3018 224.05 16.5969C235.4 18.0839 246.85 18.2758 258.3 17.5563C266.45 17.0286 274.55 15.9733 282.7 15.0619C294.35 13.7668 306 13.479 317.7 14.2944C325.4 14.966 333.05 16.0693 340.75 16.8847C352.15 18.1319 363.55 18.1319 375 17.6522C375 11.7521 375 5.90006 375 -1.23896e-08L-1.57361e-06 3.27712e-05C-1.05781e-06 5.90009 -5.46205e-07 11.7522 -3.04058e-08 17.6522Z"
      fill="white"
      id="main"
    />
  )
}

function ProductsComponent({ component }: Props): ReactElement {
  const [viewMore, setViewMore] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-assignment
  const { ref, inView } = useInView({ triggerOnce: true })
  const featuredArticleShowContext = useContext(FeaturedArticleShowContext)

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    inView &&
      featuredArticleShowContext &&
      featuredArticleShowContext.logFeaturedArticleEvent({
        event: 'products_component_imp',
      })
  }, [inView, featuredArticleShowContext])

  const productsCompoonentStyle = css`
    background-color: ${component.backgroundColor};
    color: white;
    margin: 0; /* dividerのぶん減らしている */
  `

  return (
    <Fragment>
      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
      <div ref={ref} css={productsCompoonentStyle} className="products_component">
        <div css={dividerWrapperStyle}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="18"
            width="375"
            viewBox="0 0 375 18"
            css={dividerStyle}
          >
            <ProductsComponentDivider />
          </svg>
        </div>
        <div css={productsComponentInnerStyle}>
          <h2 css={titleStyle}>
            {component.title.split(/\n/).map((leadElement, i) => (
              <Fragment key={i}>
                {leadElement}
                <br />
              </Fragment>
            ))}
          </h2>
          <ul css={productsWrapperStyle}>
            {component.products.map((product, i) => (
              <ProductComponent key={i} product={product} order={i} viewMore={viewMore} />
            ))}
          </ul>
          {!viewMore && component.products.length > 4 && (
            <a
              css={viewMoreStyle}
              onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                event.preventDefault()
                setViewMore(true)
              }}
            >
              もっと見る
            </a>
          )}
        </div>
        <div css={dividerWrapperStyle}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="18"
            width="375"
            viewBox="0 0 375 18"
            css={bottomDividerStyle}
          >
            <ProductsComponentDivider />
          </svg>
        </div>
      </div>
    </Fragment>
  );
}

const productsComponentInnerStyle = css`
  padding: 24px 12px 0 12px;
`

const dividerWrapperStyle = css`
  position: relative;
  width: 100%;
`

const dividerStyle = css`
  width: 100%;
  height: auto;
  display: block;
`

const titleStyle = css`
  ${genJyuuGothicBold}
  font-size: 20px;
  text-align: center;
  margin-top: 0;
  line-height: 30px;
`

const productsWrapperStyle = css`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
`

const viewMoreStyle = css`
  color: white;
  display: flex;
  text-decoration: none;
  font-size: 14px;
  padding: 18px;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  box-sizing: border-box;
  margin: 12px 0;
`

const bottomDividerStyle = css`
  ${dividerStyle}
  transform: scaleY(-1);
`

export default ProductsComponent
